import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignupApi } from '../api';
import { errorMessage, errorStatus, isRefusal } from '../helpers/apiErrors';
import { signupBookCallRoute, signupCompletedRoute } from '../helpers/resumeRoute';
import { useSignupStep } from '../hooks';
import { SignupState } from '../types';
import { useBookingEmbedUrl } from './SignupBookCallViewController';

/**
 * How long to wait before asking the funnel again, by attempt. Tight at first, because the ordinary case is a
 * card charged and a number ordered within seconds; slack afterwards, because what is left to wait for by then
 * is the finalization job's own retries.
 */
const POLL_DELAYS_MS = [2000, 2000, 2000, 4000, 4000, 6000, 8000];
const POLL_LAST_DELAY_MS = 10_000;

// The pace once the screen has stopped expecting a quick answer. It keeps asking rather than going quiet: the
// notice invites the client to leave the page open, and a page that stopped watching would make that a lie.
const POLL_SLOW_DELAY_MS = 30_000;

// How long before the screen stops promising a quick answer. Not a verdict on the payment — a refusal arrives
// from Stripe and shows up in the funnel — but finalization retries stretch past seven minutes, and nobody
// should be asked to watch a spinner through them.
const POLL_TIMEOUT_MS = 90_000;

/**
 * What the screen is in the middle of. `idle` also covers an attempt that ended without moving anything, and
 * `slow` is `finalizing` whose wait outlasted the screen's estimate rather than an outcome of its own.
 *
 * `stalled` is the other half of that outlasted wait: the same silence, with the money never arrived.
 */
export type SignupPaymentPhase = 'idle' | 'charging' | 'finalizing' | 'declined' | 'slow' | 'stalled';

/**
 * What became of a hold that had already lapsed when the screen opened. Taking the number again is tried
 * here rather than left to the charge: the client is about to be asked for a card, and whether the number is
 * still theirs is the one thing that decides whether asking makes any sense.
 */
export type SignupHoldRecovery = 'none' | 'reclaiming' | 'reclaimed' | 'lost';

export const useSignupPaymentViewController = () => {
  const { companyId, funnel } = useSignupStep();
  const api = useSignupApi(companyId);
  const navigate = useNavigate();
  // Read here rather than on arrival, so a funnel with no call to offer never puts that screen in the history.
  const bookingUrl = useBookingEmbedUrl();

  const [phase, setPhase] = useState<SignupPaymentPhase>('idle');
  const [canRetry, setCanRetry] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Whether the funnel has said the money arrived. `finalizing` is written by `invoice.paid` and by nothing
   * else, so this is what tells a charge being worked on from one that never happened. Never walked back:
   * money that has arrived does not stop having arrived.
   */
  const [moneyArrived, setMoneyArrived] = useState(false);
  /**
   * Registration was refused and somebody has been told about it. From here the client waits to be contacted:
   * pressing again sends the same details and alerts the team a second time about the same signup.
   */
  const [handedOver, setHandedOver] = useState(false);
  // The number this screen is about to charge for. Read from the funnel rather than passed along the route:
  // the client may well have arrived here by reloading the page.
  const [selectedNumber, setSelectedNumber] = useState<string | null>(null);
  /**
   * Whether the number is still held as far as the client is concerned, which the screen must not promise
   * without asking: ten minutes away is enough for the window to close. A closed window is not the number
   * being lost — asking for it again takes it back — and only a refusal there is.
   */
  const [numberHeld, setNumberHeld] = useState(false);
  // And until when, so the screen counts the window down instead of repeating a minutes-old answer.
  const [numberHeldUntil, setNumberHeldUntil] = useState<string | null>(null);
  const [holdRecovery, setHoldRecovery] = useState<SignupHoldRecovery>('none');

  // The screen can be left at any moment, and a timer that outlives it would poll a dead component.
  const timers = useRef<{
    interval?: ReturnType<typeof setTimeout>;
    timeout?: ReturnType<typeof setTimeout>;
  }>({});

  /**
   * Which wait is the current one. A poll reschedules itself after its answer comes back, so clearing the
   * timer is not enough on its own: the call already in flight would set a fresh one and the wait would
   * outlive whatever stopped it — a refusal, a new charge, the screen closing.
   */
  const pollGeneration = useRef(0);

  const stopPolling = useCallback(() => {
    pollGeneration.current = pollGeneration.current + 1;
    if (timers.current.interval != null) clearTimeout(timers.current.interval);
    if (timers.current.timeout != null) clearTimeout(timers.current.timeout);
    timers.current = {};
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  /**
   * The last step of the funnel, made rather than shown: nothing is left to ask the client for, so the money
   * arriving and the number being bought is the whole of it. A client who closed the tab meets this on their
   * way back — the funnel stays `provisioned` until the brand is submitted.
   */
  const finish = useCallback(async() => {
    try {
      await api.finish();
      /*
        Not the onboarding wizard — it would open on the card and the number, both of which are behind the
        client by this point. The call screen comes first where the application offers one, and leads here.
      */
      navigate(
        bookingUrl != null ? signupBookCallRoute(companyId) : signupCompletedRoute(companyId),
        { replace: true },
      );
    } catch (e) {
      /*
        A refusal is the registry's verdict on details collected several screens back, and this screen has no
        field to offer for any of them. Somebody has been told by the time it arrives, so the client is asked
        to wait rather than to press anything again.
      */
      if (isRefusal(e)) {
        setHandedOver(true);
        return;
      }
      setError(errorMessage(e, 'We could not finish your registration. Please reload the page to try again.'));
    }
  }, [api, bookingUrl, companyId, navigate]);

  // Answers whether there is anything left to wait for. A refusal and a provisioned account both end the
  // wait; the difference is only in where the client goes next.
  const applyState = useCallback((state: SignupState): boolean => {
    setSelectedNumber(state.selectedNumber);
    setNumberHeld(state.numberHeld);
    setNumberHeldUntil(state.numberHeldUntil);

    if (state.status === 'provisioned') {
      stopPolling();
      void finish();
      return true;
    }

    if (state.paymentFailed) {
      stopPolling();
      setPhase('declined');
      setCanRetry(true);
      setError(null);
      return true;
    }

    if (state.step === 'finalizing') {
      setMoneyArrived(true);
      // `slow` is never walked back to this. Both notices describe the same finalization, and the one that
      // promises a few moments stops being true once the wait has outlasted its estimate.
      setPhase(current => (current === 'slow' ? current : 'finalizing'));
    }
    return false;
  }, [finish, stopPolling]);

  /**
   * Whether the wait has already outlasted its estimate. Only the pace depends on it — the questions go on
   * either way, which is what lets a client who left the page open be carried to the next screen.
   */
  const slow = useRef(false);

  const startPolling = useCallback(() => {
    let attempt = 0;
    const generation = pollGeneration.current;
    slow.current = false;

    const poll = async() => {
      let settled = false;
      try {
        const { data } = await api.getCompanyState();

        // Before the answer is acted on, not only before the next question: a reply still in flight when the
        // client presses pay again describes the charge they have just left behind, and its refusal would
        // put the screen into `declined` over the attempt being made and stop that attempt's own wait.
        if (generation !== pollGeneration.current) return;

        settled = applyState(data);
      } catch {
        // A single failed poll says nothing; the next one asks again.
      }

      // `applyState` answers whether the wait is over — a refusal, or a number that has arrived — and stops
      // the wait itself. Rescheduling past that brings a dismissed refusal back seconds later.
      if (settled || generation !== pollGeneration.current) return;

      // Rescheduled after the answer rather than on a fixed interval, so a slow reply cannot leave two
      // requests in flight against the same funnel.
      attempt = attempt + 1;
      const delay = slow.current
        ? POLL_SLOW_DELAY_MS
        : POLL_DELAYS_MS[attempt] ?? POLL_LAST_DELAY_MS;
      timers.current.interval = setTimeout(poll, delay);
    };

    timers.current.interval = setTimeout(poll, POLL_DELAYS_MS[0]);

    timers.current.timeout = setTimeout(() => {
      // Nothing failed as far as anyone knows — the work simply outlasted the estimate. Whether that reads as
      // a wait or as a dead end is `moneyArrived`'s to say, below. Stopping the wait would leave a client who
      // did as the notice says, and kept the page open, staring at a funnel that finished minutes ago.
      slow.current = true;
      setPhase('slow');
    }, POLL_TIMEOUT_MS);
  }, [api, applyState]);

  // The funnel arrives once, but the callbacks below are rebuilt on every render, and reacting twice would
  // restart a wait that has since been stopped.
  const opened = useRef(false);

  /**
   * The read below describes the funnel as the screen found it, and once the client has asked for a charge that
   * is the older of the two stories: applying it would put the wait back over a refusal already answered.
   */
  const charged = useRef(false);

  /**
   * The whole of what the funnel says, acted on: `applyState` ends the wait, this adds the case where it is
   * under way. Only `verified` falls through, and it is the one status meaning the charge was never accepted.
   */
  const resumeFromFunnel = useCallback((state: SignupState): boolean => {
    if (applyState(state)) return true;
    if (state.status !== 'awaiting_payment') return false;

    // `idle` is the only phase worth overwriting — `applyState` has already said `finalizing` where it applies.
    setPhase(current => (current === 'idle' ? 'charging' : current));
    startPolling();
    return true;
  }, [applyState, startPolling]);

  /**
   * The hold, taken again before a card is asked for. A lapsed hold is not the number being lost — the
   * reservation is simply free to be taken once more — and only Telnyx can say which of the two it is. The
   * answer decides the screen: the number back for another half hour, or a card that must not be asked for
   * at all, because the thing it would pay for is gone.
   */
  const reclaimLapsedHold = useCallback(async(state: SignupState) => {
    if (state.numberHeld || state.selectedNumber == null) return;
    // The funnel's own word for "a reservation would be accepted": paid and finalizing funnels are past this.
    if (!state.allowedActions.includes('choose_number')) return;

    setHoldRecovery('reclaiming');
    try {
      const { data } = await api.reservePhoneNumber(state.selectedNumber);
      applyState(data);
      setHoldRecovery('reclaimed');
    } catch (e) {
      // Only a 409 says the number itself is gone. On anything else — a timeout, a 500 — the screen says
      // what it always said: the hold has lapsed, and paying takes the number again.
      setHoldRecovery(errorStatus(e) === 409 ? 'lost' : 'none');
    }
  }, [api, applyState]);

  /**
   * A reload has to find the screen where it left it, and what it left may be a charge in flight: the client
   * pressed pay, the tab was reloaded, and the money is somewhere between Stripe and the number. Reading the
   * funnel without acting on it leaves the pay button standing over a payment already gone through, which
   * the client then learns about by pressing it again.
   */
  useEffect(() => {
    if (opened.current || charged.current) return;
    opened.current = true;

    resumeFromFunnel(funnel);
    void reclaimLapsedHold(funnel);
  }, [funnel, reclaimLapsedHold, resumeFromFunnel]);

  /**
   * The request failing is not the charge failing: a checkout the API accepted and answered into a dropped
   * connection leaves the funnel charged and this screen none the wiser. Anything short of reading the row in
   * full leaves the client on a pay button the API now refuses, with a reload the only way out.
   */
  const movedOnWithoutUs = useCallback(async(): Promise<boolean> => {
    try {
      const { data } = await api.getCompanyState();
      return resumeFromFunnel(data);
    } catch {
      // No answer to act on. The message below stands, and the next page load asks again.
      return false;
    }
  }, [api, resumeFromFunnel]);

  const charge = useCallback(
    async(
      request: () => Promise<{ data: SignupState }>,
      failure: string,
      retryable: boolean,
    ) => {
      // One wait at a time. `stalled` is pressable while its own poll is still running, and left alive that
      // chain would go on asking beside the new one, at its own slow pace and with its own timeout.
      stopPolling();
      charged.current = true;
      setPhase('charging');
      setCanRetry(false);
      setError(null);
      try {
        /*
          The answer is the funnel as the API left it, and it can already say the money is in: a retry that
          found the invoice paid at Stripe settles it and answers `finalizing`. Thrown away, that has to be
          learnt again from a poll, and the polls are exactly what may be failing.
        */
        const { data } = await request();
        if (!resumeFromFunnel(data)) startPolling();
      } catch (e) {
        setPhase('idle');

        // The number went to somebody else while the client was entering the card, and nothing was charged:
        // the only thing to do is pick another number.
        if (errorStatus(e) === 409) {
          navigate('/signup/phone-number');
          return;
        }

        if (await movedOnWithoutUs()) return;

        setCanRetry(retryable);
        setError(errorMessage(e, failure));
      }
    },
    [navigate, movedOnWithoutUs, resumeFromFunnel, startPolling, stopPolling],
  );

  const pay = useCallback(
    () => charge(() => api.checkout(), 'The payment could not be started. Please try again.', false),
    [api, charge],
  );

  const retry = useCallback(
    () => charge(() => api.retryPayment(), 'The payment could not be retried. Please try again.', true),
    [api, charge],
  );

  /**
   * Everything on screen about the last refusal belongs to the card that was charged. Once another card is
   * being entered it is stale, and sitting above a fresh form it reads as a verdict on the card being typed.
   *
   * The phase goes too, not just the error line: the refusal the client actually reads comes from the phase
   * notice, and clearing only the line left "your card was declined" standing over the new card. `canRetry`
   * stays as it is — the invoice is still open, and paying it is still a retry rather than a fresh charge.
   */
  // The only way on from a number somebody else has taken.
  const chooseAnotherNumber = useCallback(() => navigate('/signup/phone-number'), [navigate]);

  const dismissError = useCallback(() => {
    setError(null);
    setPhase(current => (current === 'declined' ? 'idle' : current));
  }, []);

  /**
   * `slow` and `stalled` are one outlasted wait told apart by whether the money arrived, and only the second
   * is the client's to act on: nothing was charged, and `retry-payment` is what starts the invoicing over.
   *
   * Derived rather than decided when the wait runs out, so a charge that lands late takes the offer back on
   * its own — and so the reading cannot depend on whether a poll's answer or the timeout arrived first.
   */
  const outcome: SignupPaymentPhase = phase === 'slow' && !moneyArrived ? 'stalled' : phase;

  return {
    companyId,
    selectedNumber,
    numberHeld,
    numberHeldUntil,
    holdRecovery,
    handedOver,
    chooseAnotherNumber,
    phase: outcome,
    // Anything the client cannot act on while it lasts.
    busy: outcome === 'charging' || outcome === 'finalizing',
    canRetry: canRetry || outcome === 'stalled',
    error,
    pay,
    retry,
    dismissError,
  };
};
